import React, { useState } from 'react';
import { View, Text } from 'react-native';
import { logError } from '../../utils/logger';
import { useLocalization } from '../../store/utils/useLocalization';
import Svg from '../Svg';
import FilledButton from '../FilledButton';
import { pantryDetailsPromptStyleSheet } from '../profile/pantryDetailsPromptStyleSheet';
import { StateProgramWizard } from './StateProgramWizard';
import { StateProgramFacade } from './StateProgramFacade';
import { useStateProgramContext } from './StateProgramContext';

interface PantryDetailsPromptProps {
  programId: string;
  visitId?: number;
  registration?: {
    user?: {
      id?: number;
      [key: string]: any;
    };
    [key: string]: any;
  };
  errorReporter?: typeof logError;
}

export function PantryDetailsProgramPrompt(props: PantryDetailsPromptProps): React.ReactNode {
  const { t } = useLocalization();
  const [isWizardVisible, setWizardVisible] = useState(false);
  const errorReporter = props.errorReporter ?? logError;

  // try to load all of the program-specific dependencies before showing anything
  let programServices: StateProgramFacade;
  try {
    programServices = useStateProgramContext();
  } catch (error) {
    // report the error loading the program but skip rendering to the user
    errorReporter(`State program [${props.programId}] threw exception when loading program services`, error);
    // short-circuit and avoid showing anything if the program couldn't be loaded
    return null;
  }

  // show a prompt to the program only if it loaded successfully
  return (
    <View testID={`PantryDetailsProgramPrompt-${props.programId}`} style={pantryDetailsPromptStyleSheet.view}>
      <View style={pantryDetailsPromptStyleSheet.container}>
        <View style={pantryDetailsPromptStyleSheet.leftContainer}>
          <Svg
            icon="unverified"
            color={pantryDetailsPromptStyleSheet.icon.color}
            width={pantryDetailsPromptStyleSheet.icon.width}
            height={pantryDetailsPromptStyleSheet.icon.height}
          />
          <View style={pantryDetailsPromptStyleSheet.promptContainer}>
            <Text style={pantryDetailsPromptStyleSheet.text}>
              {t('pantry_details_prompt_title', {
                programName: programServices.name,
              })}
            </Text>
            <View style={pantryDetailsPromptStyleSheet.promptLabelContainer}>
              <Text style={pantryDetailsPromptStyleSheet.promptLabel}>
                {props.visitId
                  ? t('pantry_details_prompt_visit_label', { programName: programServices.name })
                  : t('pantry_details_prompt_without_visit_label', {
                      programName: programServices.name,
                    })}
              </Text>
            </View>
          </View>
        </View>
        {props.visitId && props.registration?.user?.id && (
          <View style={pantryDetailsPromptStyleSheet.buttonContainer}>
            <FilledButton
              onPress={() => setWizardVisible(true)}
              color={pantryDetailsPromptStyleSheet.button.color}
              label={t('program_complete_now')}
              full={false}
              paddingHorizontal={pantryDetailsPromptStyleSheet.button.paddingHorizontal}
            />
            <StateProgramWizard
              isVisible={
                /**
                 * Modals like this (and the built-in one that it wraps) have their own
                 * visibility controls to preserve state and facilitate animation.
                 * So this flag is used to control rendering rather than
                 * conditional rendering here in the parent contailer.
                 * See https://www.npmjs.com/package/react-native-modal#a-complete-example
                 */
                isWizardVisible
              }
              onClose={() => setWizardVisible(false)}
              userId={props.registration.user.id}
              visitId={props.visitId}
            />
          </View>
        )}
      </View>
    </View>
  );
}
